import DateInput from '../../components/DateInput';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { copyToClipboard } from '../../lib/clipboard';
import { ShieldCheck, Save, Camera, ExternalLink, QrCode, Copy, Check, FileText, AlertTriangle, Lock } from 'lucide-react';
import { COUNTRY_OPTIONS, REQUIRED_FIELD_LABELS } from '../../lib/idLabels';

const PRONOUN_OPTIONS = ['she/her', 'he/him', 'they/them', 'ze/zir', 'other', 'prefer not to say'];

export default function PatientProfile() {
  const [profile, setProfile] = useState(null);
  const [initial, setInitial] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [share, setShare] = useState(null);
  const [copied, setCopied] = useState('');

  const loadCompleteness = () => api.get('/patients/me/completeness').then(r => setCompleteness(r.data)).catch(()=>{});

  useEffect(() => {
    api.get('/patients/me').then(r => { setProfile(r.data); setInitial(r.data); });
    api.get('/patients/me/share-code').then(r => setShare(r.data)).catch(() => {});
    loadCompleteness();
  }, []);

  const update = (k, v) => setProfile({ ...profile, [k]: v });
  // Identity fields lock once they hold a saved value — only the clinic can change them.
  const locked = (k) => !!(initial && initial[k] != null && String(initial[k]).trim());

  const onPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 1024*1024*2) { alert('Please keep the photo under 2 MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => update('photo_url', ev.target.result);
    reader.readAsDataURL(f);
  };

  const copyCode = async (code) => {
    const ok = await copyToClipboard(code, { silent: true });
    if (ok) { setCopied(code); setTimeout(()=>setCopied(''), 1500); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/patients/me', {
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        photo_url: profile.photo_url || null,
        date_of_birth: profile.date_of_birth || null,
        sex: profile.sex || null,
        pronoun: profile.pronoun || null,
        phone: profile.phone || null,
        id_number: profile.id_number || null,
        pesel: profile.pesel || null,
        address: profile.address || null,
        city: profile.city || null,
        postal_code: profile.postal_code || null,
        country: profile.country || 'PL',
        insurance_provider: profile.insurance_provider || null,
        emergency_contact: profile.emergency_contact || null,
        emergency_contact_name: profile.emergency_contact_name || null,
        emergency_contact_phone: profile.emergency_contact_phone || null,
        emergency_contact_relation: profile.emergency_contact_relation || null,
        consent_gdpr: !!profile.consent_gdpr,
        consent_hipaa: !!profile.consent_hipaa,
      });
      setProfile(r.data); setInitial(r.data);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      loadCompleteness();
    } finally { setSaving(false); }
  };

  if (!profile) return <div className="p-8">Loading…</div>;

  const missing = new Set(completeness?.missing || []);
  const isReq = (k) => missing.has(k);

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">YOUR DATA</div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">Profile & consents</h1>
      </div>

      {completeness && !completeness.complete && (
        <div className="surface border border-[#FFB000]/40 bg-[#FFB000]/10 rounded-lg p-4 mb-4 inline-flex items-start gap-3" data-testid="profile-incomplete-banner">
          <AlertTriangle className="w-5 h-5 text-[#FFB000] flex-shrink-0 mt-0.5"/>
          <div className="text-sm">
            <strong>Complete your profile to enable booking.</strong>
            <div className="text-xs text-fg-muted mt-1">
              Still missing: {completeness.missing.map(k => REQUIRED_FIELD_LABELS[k] || k).join(' · ')}
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <div className="bento-card flex flex-col items-center text-center">
          <div className="relative mb-3">
            {profile.photo_url ? (
              <img src={profile.photo_url} alt="Patient" className="w-28 h-28 rounded-full object-cover border-2 border-soft"/>
            ) : (
              <div className="w-28 h-28 rounded-full bg-[#F2EBDB] flex items-center justify-center">
                <Camera className="w-7 h-7 text-fg-muted"/>
              </div>
            )}
            <label className="absolute -bottom-1 -right-1 btn-primary text-xs cursor-pointer inline-flex items-center gap-1 print:hidden" data-testid="profile-photo-btn">
              <Camera className="w-3.5 h-3.5"/>
              <input type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="profile-photo-input"/>
            </label>
          </div>
          <div className="font-heading font-semibold">{profile.full_name}</div>
          <div className="text-xs text-fg-muted mt-0.5">Patient ID · <span className="font-mono">{profile.patient_code || '—'}</span></div>
        </div>

        <div className="md:col-span-2 bento-card">
          <div className="flex items-center gap-2 mb-3">
            <QrCode className="w-4 h-4 text-brand"/>
            <h3 className="font-heading font-semibold">Share the clinic with friends & family</h3>
          </div>
          {share ? (
            <div className="flex flex-wrap items-center gap-3">
              <div className="surface border border-soft rounded-lg p-3">
                <div className="text-[10px] uppercase tracking-wider text-fg-muted">Invite code</div>
                <div className="font-mono tracking-[0.2em] text-xl font-bold text-brand">{share.invite_code}</div>
              </div>
              <button onClick={()=>copyCode(`${window.location.origin}/accept-invite/${share.invite_code}`)} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="copy-share-link">
                {copied ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
                {copied ? 'Copied' : 'Copy link'}
              </button>
              {navigator.share && (
                <button onClick={()=>navigator.share({title:'Physiogenic', text:'Join me at Physiogenic', url:`${window.location.origin}/accept-invite/${share.invite_code}`})} className="btn-primary text-xs" data-testid="native-share">Share</button>
              )}
            </div>
          ) : <div className="text-xs text-fg-muted">Sharing unavailable.</div>}
        </div>
      </div>

      <div className="bento-card mb-6 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-brand mt-0.5" />
        <div className="flex-1 text-sm text-fg-secondary">
          Your data is processed under the GDPR (EU 2016/679) and HIPAA-aligned controls.
          Withdraw consent anytime — your record will be archived but retained for legal medical recordkeeping (typically 20 years).
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <Link to="/legal/gdpr" className="px-2.5 py-1 rounded-full border border-brand/30 bg-brand/5 text-brand inline-flex items-center gap-1 hover:bg-brand/10 transition" data-testid="gdpr-link">
              <ShieldCheck className="w-3 h-3"/> Your GDPR rights
            </Link>
            <Link to="/legal/hipaa" className="px-2.5 py-1 rounded-full border border-brand/30 bg-brand/5 text-brand inline-flex items-center gap-1 hover:bg-brand/10 transition" data-testid="hipaa-link">
              <FileText className="w-3 h-3"/> HIPAA safeguards
            </Link>
            <Link to="/legal/privacy" className="px-2.5 py-1 rounded-full border border-brand/30 bg-brand/5 text-brand inline-flex items-center gap-1 hover:bg-brand/10 transition" data-testid="privacy-link">
              Privacy policy
            </Link>
            <a href="https://uodo.gov.pl/" target="_blank" rel="noopener noreferrer" className="px-2.5 py-1 rounded-full border border-soft text-fg-muted inline-flex items-center gap-1 hover:text-fg transition" data-testid="uodo-link">UODO (Poland) <ExternalLink className="w-3 h-3"/></a>
          </div>
        </div>
      </div>

      <div className="bento-card grid md:grid-cols-2 gap-4">
        <Field label="First name" required={isReq('first_name')} locked={locked('first_name')}>
          <input className="input" value={profile.first_name||''} onChange={e=>update('first_name', e.target.value)} disabled={locked('first_name')} data-testid="prof-first-name"/>
        </Field>
        <Field label="Last name" required={isReq('last_name')} locked={locked('last_name')}>
          <input className="input" value={profile.last_name||''} onChange={e=>update('last_name', e.target.value)} disabled={locked('last_name')} data-testid="prof-last-name"/>
        </Field>
        <Field label="Date of birth" required={isReq('date_of_birth')} locked={locked('date_of_birth')}>
          <DateInput value={profile.date_of_birth||''} onChange={e=>update('date_of_birth', e.target.value)} disabled={locked('date_of_birth')} data-testid="prof-dob"/>
        </Field>
        <Field label="Sex" required={isReq('sex')} locked={locked('sex')}>
          <select className="input" value={profile.sex || ''} onChange={e=>update('sex', e.target.value)} disabled={locked('sex')} data-testid="prof-sex">
            <option value="">— Select —</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </Field>
        <Field label="Pronoun">
          <select className="input" value={profile.pronoun || ''} onChange={e=>update('pronoun', e.target.value)} data-testid="prof-pronoun">
            <option value="">— Select —</option>
            {PRONOUN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </Field>
        <Field label="Phone" required={isReq('phone')}><input className="input" value={profile.phone||''} onChange={e=>update('phone', e.target.value)} data-testid="prof-phone"/></Field>
        <Field label="Country" required={isReq('country')}>
          <select className="input" value={profile.country||'PL'} onChange={e=>update('country', e.target.value)} data-testid="prof-country">
            {COUNTRY_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Health Care Number" required={isReq('id_number')} locked={locked('id_number') || locked('pesel')} hint="Public health / insurance card number (NFZ · NHS · Medicare · Krankenkasse etc.)">
          <input className="input font-mono"
                 value={profile.id_number || profile.pesel || ''}
                 onChange={e=>update('id_number', e.target.value)}
                 disabled={locked('id_number') || locked('pesel')}
                 data-testid="prof-id"/>
        </Field>
        <Field label="Street address" full required={isReq('address')}><input className="input" value={profile.address||''} onChange={e=>update('address', e.target.value)} data-testid="prof-address"/></Field>
        <Field label="City" required={isReq('city')}><input className="input" value={profile.city||''} onChange={e=>update('city', e.target.value)} data-testid="prof-city"/></Field>
        <Field label="Postal code" required={isReq('postal_code')}><input className="input" value={profile.postal_code||''} onChange={e=>update('postal_code', e.target.value)} data-testid="prof-postal"/></Field>
        <Field label="Insurance provider"><input className="input" value={profile.insurance_provider||''} onChange={e=>update('insurance_provider', e.target.value)} data-testid="prof-insurance"/></Field>
        <Field label="Emergency contact name"><input className="input" value={profile.emergency_contact_name||''} onChange={e=>update('emergency_contact_name', e.target.value)} data-testid="prof-emg-name"/></Field>
        <Field label="Emergency contact phone"><input className="input" value={profile.emergency_contact_phone||''} onChange={e=>update('emergency_contact_phone', e.target.value)} data-testid="prof-emg-phone"/></Field>
        <Field label="Emergency contact relation"><input className="input" value={profile.emergency_contact_relation||''} onChange={e=>update('emergency_contact_relation', e.target.value)} placeholder="Spouse, parent, sibling…" data-testid="prof-emg-rel"/></Field>

        <div className="md:col-span-2 space-y-3 mt-4 surface border border-soft rounded-lg p-4">
          <Toggle checked={!!profile.consent_gdpr} onChange={v=>update('consent_gdpr', v)} testid="consent-gdpr">
            I consent to GDPR data processing for medical care purposes.
          </Toggle>
          <Toggle checked={!!profile.consent_hipaa} onChange={v=>update('consent_hipaa', v)} testid="consent-hipaa">
            I consent to HIPAA-aligned medical record handling.
          </Toggle>
        </div>

        <div className="md:col-span-2 flex justify-end items-center gap-3">
          {saved && <span className="text-sm text-[#3A664D]">Saved.</span>}
          <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="save-profile">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full, required, hint, locked }) {
  return (
    <label className={full ? 'md:col-span-2 block' : 'block'}>
      <div className="text-xs text-fg-muted mb-1.5 flex items-center gap-1">
        {label}
        {required && <span className="text-[#B83A3A]" title="Required to book appointments">*</span>}
        {locked && <Lock className="w-3 h-3 text-fg-muted" title="Locked — contact your clinic to change this" />}
      </div>
      {children}
      {locked && <div className="text-[10px] text-fg-muted mt-1">Locked for record integrity — contact your clinic to change.</div>}
      {hint && !locked && <div className="text-[10px] text-fg-muted mt-1">{hint}</div>}
    </label>
  );
}

function Toggle({ checked, onChange, testid, children }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e=>onChange(e.target.checked)} data-testid={testid} className="w-4 h-4" />
      <span className="text-sm">{children}</span>
    </label>
  );
}
